import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { LangProvider } from './LangContext';
import { auth } from './firebase';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import WorkReviewsPage from './pages/WorkReviewsPage';
import ComplaintsPage from './pages/ComplaintsPage';
import WorkersPage from './pages/WorkersPage';
import CemeteriesPage from './pages/CemeteriesPage';
import ServicePackagesPage from './pages/ServicePackagesPage';
import FlowerCatalogPage from './pages/FlowerCatalogPage';
import PromoCodesPage from './pages/PromoCodesPage';
import NewUsersPage from './pages/NewUsersPage';
import ClientsPage from './pages/ClientsPage';
import CommunicationsPage from './pages/CommunicationsPage';
import SupportInboxPage from './pages/SupportInboxPage';
import FinancePage from './pages/FinancePage';
import AdminAccessPage from './pages/AdminAccessPage';
import ActivateAccountPage from './pages/ActivateAccountPage';
import { normalizeAdmin } from './adminAccess';

function ProtectedRoute({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const [user, setUser] = useState(undefined); // undefined = loading

  useEffect(() => {
    // Firebase Auth persists the session itself. Never store a password.
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (!fbUser) {
        localStorage.removeItem('admin_user');
        setUser(null);
        return;
      }
      try {
        const saved = localStorage.getItem('admin_user');
        const parsed = saved ? normalizeAdmin(JSON.parse(saved)) : null;
        if (parsed?.email && fbUser.email &&
            parsed.email.toLowerCase() === fbUser.email.toLowerCase()) {
          const { password, ...safe } = parsed;  // strip any legacy stored password
          localStorage.setItem('admin_user', JSON.stringify(safe));
          setUser(safe);
        } else {
          localStorage.removeItem('admin_user');
          setUser(null);
        }
      } catch {
        localStorage.removeItem('admin_user');
        setUser(null);
      }
    });
    return () => unsub();
  }, []);

  const handleLogin = (u) => {
    const { password, ...safe } = normalizeAdmin(u);   // never persist a password
    localStorage.setItem('admin_user', JSON.stringify(safe));
    setUser(safe);
  };

  const handleLogout = async () => {
    localStorage.removeItem('admin_user');
    try { await signOut(auth); } catch (e) { console.error(e); }
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <LangProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={
          user ? <Navigate to="/" /> : <LoginPage onLogin={handleLogin} />
        } />
        <Route path="/activate" element={<ActivateAccountPage />} />
        <Route path="/" element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage user={user} />} />
          <Route path="orders" element={<OrdersPage user={user} />} />
          <Route path="work-reviews" element={<WorkReviewsPage user={user} />} />
          <Route path="complaints" element={<ComplaintsPage user={user} />} />
          <Route path="workers" element={<WorkersPage user={user} />} />
          <Route path="clients" element={<ClientsPage user={user} />} />
          <Route path="cemeteries" element={<CemeteriesPage user={user} />} />
          <Route path="packages" element={<ServicePackagesPage user={user} />} />
          <Route path="flowers" element={<FlowerCatalogPage user={user} />} />
          <Route path="promo-codes" element={<PromoCodesPage user={user} />} />
          <Route path="users" element={<NewUsersPage user={user} />} />
          <Route path="communications" element={<CommunicationsPage user={user} />} />
          <Route path="support" element={<SupportInboxPage user={user} />} />
          <Route path="finance" element={<FinancePage user={user} />} />
          <Route path="admin-access" element={<AdminAccessPage user={user} />} />
        </Route>
      </Routes>
    </BrowserRouter>
    </LangProvider>
  );
}
